import { Signal } from "signals";
import { BigWinTierConfig } from "slotworks/config/component-config-schema";
import { ButtonEvent } from "slotworks/graphics/elements/button-element";
import Layers from "slotworks/graphics/layers/layers";
import BitmapText from "slotworks/graphics/pixi/bitmap-text";
import { Services } from "slotworks/services/services";
import { SoundEvent } from "slotworks/services/sound/sound-events";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import Timer from "slotworks/utils/timer";
import * as TweenJS from "slotworks/utils/tween";

export default class BigWinComponent {

    public static BACKGROUND_SCENE: string = "bigWin";
    public static FOREGROUND_SCENE: string = "bigWin";

    public static HANG_TIME: number = 3000;
    public static CLICK_TO_SKIP: boolean = true;
    public static INSTANT_UPGRADE: boolean = true;

    public countupMode: CountMode;

    public upgrade: boolean;

    public totalReached: Signal;

    private tiers: BigWinTierConfig[] = [];
    private currentTiers: BigWinTierConfig[];
    private valueTweenObject: { value: number };

    constructor() {
        this.totalReached = new Signal();
    }

    public format: (value: number) => string = (value) => Services.currency.format(value);

    public addWinTier(config: BigWinTierConfig) {

        this.tiers.push(config);

        this.tiers.sort((a, b) => a.threshold - b.threshold);
    }

    public upgradePending() {
        return this.currentTiers.length > 1;
    }

    public showWin(amount: number, stake: number): Contract<boolean> {

        return new Contract<boolean>((resolve) => {

            if (this.isBigWin(amount, stake)) {
                this.currentTiers = this.getSelectedTiers(amount, stake);

                let waitTimer: number;
                let countTimer: number;

                const userSkip = () => {
                    TweenJS.removeAll(this.valueTweenObject);
                    Timer.clearTimeout(waitTimer);
                    Timer.clearTimeout(countTimer);

                    if (this.currentTiers.length > 1) {
                        this.currentTiers = [this.currentTiers[this.currentTiers.length - 1]];

                        upgrade(true);
                    } else if (this.currentTiers.length === 0) {
                        out();
                    } else {
                        waitTimer = Timer.setTimeout(out, this.currentTiers[0].waitTime);
                        this.currentTiers.shift();
                    }

                    const valueText = this.getValueText();
                    if (valueText) {
                        valueText.text = this.format(amount);
                    }

                    Layers.CelebrationContent.skipTransition();

                    if (this.valueTweenObject) {
                        Services.sound.event(SoundEvent.big_win_countup_end);
                        this.valueTweenObject = null;
                    }
                };

                const out = () => {
                    document.body.removeEventListener(ButtonEvent.POINTER_DOWN.getDOMEventString(), userSkip, true);
                    Services.sound.customEvent("celebration_end");
                    Layers.CelebrationContent.defaultScene().then(() => {
                        new Parallel([
                            () => Layers.CelebrationForeground.defaultScene(),
                            () => Layers.CelebrationBackground.defaultScene()
                        ]).then(() => {
                            resolve(true);
                        });
                    });
                };

                const upgrade = (skip?: boolean) => {
                    const currentTier = this.currentTiers[0];

                    if (BigWinComponent.INSTANT_UPGRADE || skip) {
                        Layers.CelebrationContent.jumpToScene("default");
                    }

                    Layers.CelebrationContent.setScene(currentTier.scene).execute();

                    countTimer = Timer.setTimeout(() => {
                        if (this.upgrade) {
                            this.currentTiers.shift();
                        }

                        if (this.currentTiers.length && this.upgrade) {
                            upgrade();
                        } else {
                            waitTimer = Timer.setTimeout(out, currentTier.waitTime);
                        }
                    }, skip ? 0 : currentTier.countTime);
                };

                this.startCountup(amount, stake);
                upgrade();

                if (BigWinComponent.CLICK_TO_SKIP) {
                    document.body.addEventListener(ButtonEvent.POINTER_DOWN.getDOMEventString(), userSkip, true);
                }
            } else {
                resolve(false);
            }

        });
    }

    public isBigWin(amount: number, stake: number): boolean {
        const multiplier = amount / stake;

        const selectedLevel = this.getSelectedTier(amount, stake);

        return selectedLevel !== null && selectedLevel.threshold <= multiplier;
    }

    private startCountup(amount: number, stake: number) {
        const selectedLevels = this.getSelectedTiers(amount, stake);

        let valueText: PIXI.Text | BitmapText;

        this.valueTweenObject = { value: 0 };
        const countTween: TweenJS.Tween = new TweenJS.Tween(this.valueTweenObject);

        const update = () => {
            valueText = this.getValueText();
            if (valueText) {
                valueText.text = this.format(this.valueTweenObject.value);
            }
        };

        const complete = () => {
            valueText = this.getValueText();
            if (valueText) {
                valueText.text = this.format(amount);
            }

            Services.sound.event(SoundEvent.big_win_countup_end);

            this.totalReached.dispatch();

            this.valueTweenObject = null;
        };

        if (this.countupMode === CountMode.LINEAR) {

            let countTime = 0;
            for (const level of selectedLevels) {
                countTime += level.countTime;
            }
            countTween.to({ value: amount }, countTime);
            countTween.onUpdate(update);
            countTween.onComplete(complete);

        } else {

            const finalLevel = selectedLevels.pop();
            let tween = countTween;
            countTween.to({ value: 0 }, 0);

            for (const level of selectedLevels) {
                tween.chain(tween = new TweenJS.Tween(this.valueTweenObject).to({ value: level.threshold * stake }, level.countTime).onUpdate(update));
            }

            tween.chain(new TweenJS.Tween(this.valueTweenObject).to({ value: amount }, finalLevel.countTime).onUpdate(update).onComplete(complete));
        }

        const setup = () => {
            valueText = this.getValueText();
            if (valueText) {
                valueText.text = "";
            }

            Services.sound.event(SoundEvent.big_win_countup_start);
        };

        if (Layers.CelebrationBackground.hasScene(BigWinComponent.BACKGROUND_SCENE)) {
            Layers.CelebrationBackground.setScene(BigWinComponent.BACKGROUND_SCENE).execute();
        }
        if (Layers.CelebrationForeground.hasScene(BigWinComponent.FOREGROUND_SCENE)) {
            Layers.CelebrationForeground.setScene(BigWinComponent.FOREGROUND_SCENE, setup).execute();
        }

        countTween.start();
    }

    private getSelectedTier(amount: number, stake: number): BigWinTierConfig {
        const levels = this.getSelectedTiers(amount, stake);

        return levels.length ? levels[levels.length - 1] : null;
    }

    private getSelectedTiers(amount: number, stake: number): BigWinTierConfig[] {
        const multiplier = amount / stake;

        let selectedLevels: BigWinTierConfig[] = [];

        for (const level of this.tiers) {
            if (level.threshold > multiplier) {
                break;
            } else {
                if (this.upgrade) {
                    selectedLevels.push(level);
                } else {
                    selectedLevels = [level];
                }
            }
        }

        return selectedLevels;
    }

    private getValueText() {
        return Layers.CelebrationContent.getText("value")
            || Layers.CelebrationContent.getBitmapText("value")
            || Layers.CelebrationForeground.getText("value")
            || Layers.CelebrationForeground.getBitmapText("value")
            || Layers.CelebrationBackground.getText("value")
            || Layers.CelebrationBackground.getBitmapText("value")
            || null;
    }
}

export enum CountMode {
    LINEAR = "linear",
    TIERED = "tiered"
}
